import { graphql } from "babel-plugin-relay/macro";
import dayjs from "dayjs";
import { useCallback, useContext } from "react";
import { useMutation } from "react-relay";
import {
  PayloadError,
  RecordSourceSelectorProxy,
  UploadableMap,
} from "relay-runtime";
import {
  GraphQLNotificationInputPartial,
  useUpdateNotificationMutation,
  useUpdateNotificationMutation$data,
} from "./__generated__/useUpdateNotificationMutation.graphql";
import SnackbarContext from "../contexts/SnackbarContext";
import { getNotificationTabConnections } from "../views/dashboard/engage/NotificationsTab";

const updateMutation = graphql`
  mutation useUpdateNotificationMutation(
    $input: GraphQLNotificationInputPartial!
  ) {
    updateNotification(input: $input) {
      ... on GraphQLNotification {
        id
        landingPath
        ...NotificationTableRow_notification
      }
    }
  }
`;

type Params = {
  input: GraphQLNotificationInputPartial;
  onCompleted?: (
    data: useUpdateNotificationMutation$data,
    errors: PayloadError[] | null
  ) => void;
  onError?: (error: Error) => void;
  uploadables?: UploadableMap;
  updater?: (
    store: RecordSourceSelectorProxy,
    data: useUpdateNotificationMutation$data
  ) => void;
};

const useUpdateNotification = ({
  brandID,
  showSnackbar = true,
}: {
  brandID: string;
  showSnackbar?: boolean;
}): [(params: Params) => void, boolean] => {
  const [mutation, isUpdating] =
    useMutation<useUpdateNotificationMutation>(updateMutation);
  const snackbarContext = useContext(SnackbarContext);
  const onMutate = useCallback(
    ({ input, onCompleted, onError, updater, uploadables }: Params) => {
      mutation({
        variables: { input },
        onCompleted: (data, errors) => {
          onCompleted?.(data, errors);
          if (!errors && showSnackbar) {
            snackbarContext?.openSnackbar("Update Successful", "success");
          }
        },
        onError: (error: Error) => {
          onError?.(error);
          if (showSnackbar) {
            snackbarContext?.openSnackbar("Update Failed", "error");
          }
        },
        updater: (store, data) => {
          updater?.(store, data);

          const brand = store.get(brandID);
          if (brand == null) {
            return;
          }
          const notifConnection = getNotificationTabConnections(brand)[0];
          if (notifConnection == null) {
            return;
          }
          const sortedEdges = notifConnection
            .getLinkedRecords("edges")
            ?.sort((a, b) => {
              const aTime = dayjs(
                a.getLinkedRecord("node")?.getValue("timestamp") + ""
              ).unix();
              const bTime = dayjs(
                b.getLinkedRecord("node")?.getValue("timestamp") + ""
              ).unix();

              return bTime - aTime;
            });
          if (sortedEdges == null) {
            return;
          }
          notifConnection.setLinkedRecords(sortedEdges, "edges");
        },
        uploadables,
      });
    },
    [mutation, brandID, showSnackbar, snackbarContext]
  );
  return [onMutate, isUpdating];
};

export default useUpdateNotification;
